import { useCallback, useMemo, useState } from 'react';

import { PopoverTrigger } from '@radix-ui/react-popover';
import { PaginationState } from '@tanstack/react-table';
import { useParams } from 'react-router-dom';
import { useDebounce } from 'use-debounce';

import { EntityFilters } from './components/entity-filters';
import { EntityLoader } from './components/entity-loader';
import { EntityRow } from './components/entity-row';
import { ButtonGroup } from '../../../../../../components/shadcn/button-group';
import { useMatterProcessingStore } from '../../../../../../stores/matter-processing-store';
import { Permission } from '@/../../backend/src/auth/permissions/permission';
import { PersonType } from '@/../../backend/src/types/types';
import { DocumentDownloadOptions, DownloadFormat, StyleFormat } from '@/../../backend/src/types/types';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useSubsetPersonsDownload } from '@/api/mutations/useDownloadSubsetPersons';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useGetSubset } from '@/api/queries/useGetSubset';
import { useGetSubsetPersons, PersonFilters } from '@/api/queries/useGetSubsetPersons';
import { capitalizeFirstLetter } from '@/helpers/filterNames';
import { PermissionGate } from '@/hooks/useHasPermission';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { DownloadFlow } from '@/new/components/download/DownloadFlow';
import type { DownloadDefinition, SyncVariant } from '@/new/components/download/types';
import { useDownloadController } from '@/new/components/download/useDownloadController';
import { FilterPills } from '@/new/components/filter-pills';
import { Icon } from '@/new/components/icon';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { Pagination } from '@/new/components/pagination';
import { SearchInput } from '@/new/components/search-input';
import { Button } from '@/new/components/shadcn/button';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/new/components/shadcn/empty';
import { Popover } from '@/new/components/shadcn/popover';
import { useSubsetBreadcrumbs } from '@/new/hooks/use-subset-breadcrumbs';
import { CHIP_COLOURS } from '@/new/lib/chip-colours';
import { FilterPill } from '@/utils/buildFilterPills';

const defaultFilters: PersonFilters = {};

export function Entities() {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) return null;

  const { data: matter } = useGetMatterV2(matterId);
  const { data: subset } = useGetSubset(matterId, subsetId);

  const breadcrumbItems = useSubsetBreadcrumbs({
    matterId,
    subsetId,
    matter,
    subset,
    currentPage: { label: 'Entities', path: 'entities' },
  });

  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 25 });
  const [filters, setFilters] = useState<PersonFilters>(defaultFilters);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch] = useDebounce(searchInput, 300);
  const download = useDownloadController();

  const { data: response, isLoading } = useGetSubsetPersons({
    matterId,
    subsetId,
    searchQuery: debouncedSearch,
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    paginated: true,
    filters,
  });

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
    setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
  };

  const handleTypesChange = (types: PersonType[]) => {
    setFilters({ ...filters, types: types.length > 0 ? types : undefined });
    setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
  };

  const clearAllFilters = () => {
    setSearchInput('');
    setFilters(defaultFilters);
    setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
  };

  const handleRemoveFilter = (filterKey: string, value?: string) => {
    if (filterKey === 'types' && value) {
      const newTypes = (filters.types ?? []).filter((t) => t !== value);
      setFilters({ ...filters, types: newTypes.length > 0 ? newTypes : undefined });
    }
    setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
  };

  // Pagination
  const goToPage = (val: number) => setPagination({ pageIndex: val, pageSize: pagination.pageSize });
  const prevPage = () => goToPage(pagination.pageIndex - 1);
  const nextPage = () => goToPage(pagination.pageIndex + 1);
  const handlePageSizeChange = (newPageSize: number) => setPagination({ pageIndex: 0, pageSize: newPageSize });

  // Filter pills
  const filterPills = useMemo((): FilterPill[] => {
    const pills: FilterPill[] = [];
    if (filters.types) {
      filters.types.forEach((type) => {
        pills.push({
          id: `type-${type}`,
          label: capitalizeFirstLetter(type),
          color: CHIP_COLOURS.type,
          onRemove: () => handleRemoveFilter('types', type),
        });
      });
    }
    return pills;
  }, [filters.types]);

  const { mutateAsync: downloadPersons } = useSubsetPersonsDownload({ matterId, subsetId });

  const downloadDefinition = useMemo<DownloadDefinition>(() => {
    const tableVariant: SyncVariant<DownloadFormat> = {
      id: 'entities-table',
      kind: 'table',
      label: 'Table',
      mode: 'sync',
      formats: [DownloadFormat.EXCEL, DownloadFormat.DOCX],
      defaultFilename: 'Persons',
      columns: { available: personsDownloadColumnOptions, default: personsDownloadColumnOptions },
      mutate: async (values) => {
        trackCustomEvent(
          values.format === DownloadFormat.EXCEL
            ? TrackingEventsV2.DocumentDownloadPersonsCsv
            : TrackingEventsV2.DocumentDownloadPersonsDocx,
          { matterId, isAllDocuments: subset?.isAllDocuments },
        );
        await downloadPersons({
          title: values.filename,
          columns: values.columns ?? [],
          styleFormat: StyleFormat.Table,
          downloadFormat: values.format,
        });
      },
    };

    const writtenVariant: SyncVariant<DownloadFormat> = {
      id: 'entities-written',
      kind: 'written',
      label: 'Written',
      mode: 'sync',
      formats: [DownloadFormat.DOCX],
      defaultFilename: 'Persons',
      docOptions: true,
      mutate: async (values) => {
        trackCustomEvent(TrackingEventsV2.DocumentDownloadPersonsWritten, {
          matterId,
          isAllDocuments: subset?.isAllDocuments,
        });
        await downloadPersons({
          title: values.filename,
          styleFormat: StyleFormat.Written,
          downloadFormat: values.format,
          documentOptions: values.docOptions,
        });
      },
    };

    return { variants: [tableVariant, writtenVariant] };
  }, [downloadPersons, matterId, subset?.isAllDocuments]);

  const { getProcessingState } = useMatterProcessingStore();
  const hasFilters = (filters.types && filters.types.length > 0) || searchInput !== '';

  return (
    <div className="flex h-full min-w-full flex-col">
      <Breadcrumb items={breadcrumbItems} />

      <div className="flex h-full flex-col overflow-hidden">
        <PageContainer>
          <PageHeader>
            <PageTitle text="Entities" showSubsetChip />

            <Pagination
              canGetPrevPage={pagination.pageIndex > 0}
              canGetNextPage={pagination.pageIndex < (response?.pageCount ?? 0) - 1}
              prevPage={prevPage}
              nextPage={nextPage}
              currentPage={pagination.pageIndex}
              noOfPages={response?.pageCount}
              goToPage={goToPage}
              pageSize={pagination.pageSize}
              onPageSizeChange={handlePageSizeChange}
              totalCount={response?.totalUnfilteredCount}
              filteredCount={response?.totalCount}
              entityName="entities"
            />
          </PageHeader>

          <div className="flex w-full gap-2">
            <SearchInput
              placeholder="Search entities..."
              className="grow"
              value={searchInput}
              onChange={handleSearchChange}
            />

            <PermissionGate
              permission={Permission.PersonDownload}
              uiMode="disabled"
              disabledMessage="You do not have permission to download entities"
            >
              <Button onClick={download.open} disabled={!getProcessingState(matterId)?.hasCompletedRuns}>
                <Icon className="lucide--download" />
                <span>Download</span>
              </Button>
              <DownloadFlow controller={download} definition={downloadDefinition} />
            </PermissionGate>

            <ButtonGroup>
              <Popover>
                <PopoverTrigger asChild>
                  <Button>
                    <Icon className="lucide--funnel-plus" />
                    <span>Add Filters</span>
                  </Button>
                </PopoverTrigger>
                <EntityFilters
                  selectedTypes={filters.types ?? []}
                  onTypesChange={handleTypesChange}
                  clearAllFilters={clearAllFilters}
                />
              </Popover>

              <Button onClick={clearAllFilters} disabled={!hasFilters}>
                <Icon className="lucide--funnel-x" />
                <span>Clear All Filters</span>
              </Button>
            </ButtonGroup>
          </div>

          <div className="my-2.5">
            <FilterPills pills={filterPills} />
          </div>
        </PageContainer>

        {isLoading ? (
          <div className="mx-5 flex flex-col gap-5 overflow-auto">
            {Array.from({ length: 5 }).map((_, index) => (
              <EntityLoader key={index} />
            ))}
          </div>
        ) : (response?.persons.length ?? 0) === 0 ? (
          <div className="flex items-center justify-center p-6">
            <Empty>
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <Icon className="lucide--user-round h-5 w-5" />
                </EmptyMedia>
                <EmptyTitle>No entities found</EmptyTitle>
                <EmptyDescription>Sorry, we couldn't find any entities matching these criteria.</EmptyDescription>
              </EmptyHeader>
            </Empty>
          </div>
        ) : (
          <div className="mt-1 flex flex-col gap-4 overflow-auto px-8 pb-8">
            {response?.persons.map((person) => (
              <EntityRow key={person.id} person={person} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const personsDownloadColumnOptions = ['Name', 'Type', 'Aliases', 'Description'];
